import { downloadBlobFromApi, openBlobFromApi, printBlobFromApi } from '../../helpers'
import apiService from '../Api'
import IFilter, {
  CancelContestApplicationResponse,
  IArt,
  IArtEvaluationByEvaluator,
  IArtEvaluationsBulkPayload,
  IApplicantSelectOption,
  IArtworkEvaluationFilter,
  IArtworkShowcasePayload,
  IArtworkShowcaseState,
  IShowcaseTakenPosition,
  IContest,
  IContestApplication,
  IContestApplicationComplete,
  IContestApplicationsFilter,
  IContestApplicationWithArts,
  IContestApprovedArtwork,
  IContestArtwork,
  IContestArtworksFilter,
  IContestArtworkForEvaluation,
  IContestBackgroundsPayload,
  IContestComplete,
  IContestDetail,
  IContestForSelect,
  IContestManualApplicationPayload,
  IContestManualApplicationResponse,
  IContestRegistrationPayload,
  IContestsStorePayload,
  IContestsUpdatePayload,
  IExhibitorCertificatesPayload,
  IExhibitorCertificatesStats,
  IPickupEmailsStats
} from './interfaces'

export default class ContestsService {
  async index(filter: IFilter): Promise<{
    current_page: number
    per_page: number
    total: number
    total_filtered: number
    data: IContest[]
  }> {
    const params = new URLSearchParams()
    params.append('search', filter.search)
    params.append('date_start', filter.date.start)
    params.append('date_end', filter.date.end)

    params.append('page_size', filter.page_size.toString())
    params.append('page', filter.page.toString())

    return apiService.get(`/contests`, { params }).then((response) => response.data)
  }

  async show(id: number): Promise<IContestComplete> {
    return apiService.get(`/contests/${id}`).then((response) => response.data)
  }

  async store(data: IContestsStorePayload) {
    return apiService.post(`/contests/`, data)
  }

  async update(id: number, data: IContestsUpdatePayload) {
    return apiService.put(`/contests/${id}`, data)
  }

  /** Uploads, replaces or removes the step backgrounds. Separate from `update` because the contest itself is saved as JSON. */
  async updateBackgrounds(id: number, payload: IContestBackgroundsPayload) {
    const formData = new FormData()
    if (payload.registration_background) formData.append('registration_background', payload.registration_background)
    if (payload.submission_background) formData.append('submission_background', payload.submission_background)
    if (payload.menu_background) formData.append('menu_background', payload.menu_background)
    if (payload.remove_registration_background) formData.append('remove_registration_background', '1')
    if (payload.remove_submission_background) formData.append('remove_submission_background', '1')
    if (payload.remove_menu_background) formData.append('remove_menu_background', '1')

    return apiService.post(`/contests/${id}/backgrounds`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  }

  async delete(id: number) {
    return apiService.delete(`/contests/${id}`)
  }

  /** Toggles whether the contest's approved artworks appear in the public gallery. */
  async updateGalleryVisibility(id: number, showInGallery: boolean): Promise<void> {
    return apiService.patch(`/contests/${id}/gallery-visibility`, { show_in_gallery: showInGallery })
  }

  async getBySlug(slug: string): Promise<IContestComplete> {
    return apiService.get(`/contests/slug/${slug}`).then((response) => response.data)
  }

  async getDetailBySlug(slug: string): Promise<IContestDetail> {
    return apiService.get(`/contests/dashboard/slug/${slug}`).then((response) => response.data)
  }

  /** Returns a lightweight list of all contests (id, name, slug) for use in select/search inputs. */
  async listForSelect(): Promise<IContestForSelect[]> {
    return apiService.get('/contests/select').then((response) => response.data)
  }

  /** Returns approved artworks (approved or available_for_auction) for a specific contest. */
  async listApprovedArtworks(id: number): Promise<IContestApprovedArtwork[]> {
    return apiService.get(`/contests/${id}/approved-artworks`).then((response) => response.data)
  }

  /** Downloads a XLSX file containing the list of participants for a specific contest. */
  async downloadParticipantsXlsx(id: number): Promise<void> {
    return downloadBlobFromApi(`/contests/${id}/participants-xlsx`, `concurso_${id}_participantes.xlsx`)
  }

  async registerApplication(id: number, data: IContestRegistrationPayload, lastPaymentUuid: string | null) {
    const formData = new FormData()

    if (data.profile_image) {
      formData.append('profile_image', data.profile_image)
    }

    formData.append('name', data.name)
    formData.append('email', data.email)
    formData.append('email_confirmation', data.email_confirmation)
    formData.append('phone', data.phone)
    formData.append('phone_confirmation', data.phone_confirmation)
    formData.append('cpf', data.cpf)
    formData.append('instagram', data.instagram)

    formData.append('course_id', data.course_id.toString())
    formData.append('teacher_id', data.teacher_id.toString())

    formData.append('rules_accepted', data.rules_accepted ? '1' : '0')

    formData.append('payment_method', data.payment_method)

    if (data.payment_method === 'credit_card') {
      formData.append('credit_card[number]', data.credit_card.number)
      formData.append('credit_card[name]', data.credit_card.name)
      formData.append('credit_card[expiration_month]', data.credit_card.expiration_month)
      formData.append('credit_card[expiration_year]', data.credit_card.expiration_year)
      formData.append('credit_card[cvv]', data.credit_card.cvv)
      formData.append('credit_card[installments]', data.credit_card.installments.toString())
    }

    if (lastPaymentUuid) {
      formData.append('uuid', lastPaymentUuid)
    }

    return apiService
      .post(`/contests/${id}/register-application`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((response) => response.data)
  }

  async getPaymentStatus(uuid: string): Promise<{ status: string }> {
    return apiService.get(`/contests/payment-status/${uuid}`).then((response) => response.data)
  }

  async loginWithCpf(contestId: number, cpf: string) {
    return apiService.post(`/contests/${contestId}/login`, { cpf }).then((response) => response.data)
  }

  async submitArts(contestApplicationId: number, cpf: string, arts: IArt[]) {
    const formData = new FormData()

    formData.append('cpf', cpf)

    arts.forEach((art, index) => {
      formData.append(`arts[${index}][name]`, art.name)
      formData.append(`arts[${index}][technique]`, art.technique)
      formData.append(`arts[${index}][dimensions]`, art.dimensions)
      formData.append(`arts[${index}][available_for_auction]`, art.available_for_auction ? '1' : '0')
      formData.append(`arts[${index}][starting_bid]`, art.available_for_auction ? art.starting_bid.toString() : '0')

      if (art.art_image) {
        formData.append(`arts[${index}][art_image]`, art.art_image)
      }
    })

    return apiService.post(`/contests/${contestApplicationId}/submit-arts`, formData).then((response) => response.data)
  }

  async submitSingleArt(contestApplicationId: number, cpf: string, art: IArt) {
    const formData = new FormData()

    formData.append('cpf', cpf)
    formData.append('arts[0][name]', art.name)
    formData.append('arts[0][technique]', art.technique)
    formData.append('arts[0][dimensions]', art.dimensions)
    formData.append('arts[0][available_for_auction]', art.available_for_auction ? '1' : '0')
    formData.append('arts[0][starting_bid]', art.available_for_auction ? art.starting_bid.toString() : '0')

    if (art.art_image) {
      formData.append('arts[0][art_image]', art.art_image)
    }

    return apiService.post(`/contests/${contestApplicationId}/submit-arts`, formData).then((response) => response.data)
  }

  async listApplications(filter: IContestApplicationsFilter): Promise<{
    current_page: number
    per_page: number
    total: number
    total_filtered: number
    data: IContestApplication[]
  }> {
    const params = new URLSearchParams()
    params.append('contest_name', filter.contest_name)
    params.append('payment_status', filter.payment_status)
    params.append('applicant_name', filter.applicant_name)

    params.append('page_size', filter.page_size.toString())
    params.append('page', filter.page.toString())

    return apiService.get(`/contests/applications`, { params }).then((response) => response.data)
  }

  async listApplicationsByContest(contestId: number): Promise<IContestApplicationWithArts[]> {
    return apiService.get(`/contests/applications/${contestId}`).then((response) => response.data)
  }

  async getApplicationDetails(applicationId: number): Promise<IContestApplicationComplete> {
    return apiService.get(`/contests/application/${applicationId}`).then((response) => response.data)
  }

  async getPixCodes(uuid: string) {
    return apiService.get(`/contests/pix-codes/${uuid}`).then((response) => response.data)
  }

  async getAllForSelect(): Promise<IContestForSelect[]> {
    return apiService.get(`/contests/all-for-select`).then((response) => response.data)
  }

  async checkCpf(contestId: number, cpf: string): Promise<{ has_application: boolean }> {
    return apiService.post(`/contests/${contestId}/check-cpf`, { cpf }).then((response) => response.data)
  }

  async listArtworks(filter: IContestArtworksFilter): Promise<{
    current_page: number
    per_page: number
    total: number
    total_filtered: number
    data: IContestArtwork[]
  }> {
    const params = new URLSearchParams()
    params.append('contest_name', filter.contest_name)
    params.append('applicant_name', filter.applicant_name)
    if (filter.exhibition_status) params.append('exhibition_status', filter.exhibition_status)
    if (filter.sale_status) params.append('sale_status', filter.sale_status)
    if (filter.available_for_auction) params.append('available_for_auction', filter.available_for_auction)
    if (filter.delivery_status) params.append('delivery_status', filter.delivery_status)
    if (filter.pickup_email_status) params.append('pickup_email_status', filter.pickup_email_status)
    params.append('page_size', filter.page_size.toString())
    params.append('page', filter.page.toString())

    return apiService.get(`/contests/artworks`, { params }).then((response) => response.data)
  }

  async updateArtExhibitionStatus(
    artId: number,
    exhibitionStatus: 'approved' | 'rejected' | 'pending' | 'stand_by'
  ): Promise<void> {
    return apiService.put(`/contests/artworks/${artId}/exhibition-status`, { exhibition_status: exhibitionStatus })
  }

  async updateArtFields(
    artId: number,
    data: {
      receipt_date?: string | null
      return_date?: string | null
      sale_status?: string
      frame_value?: number | null
    }
  ): Promise<void> {
    return apiService.put(`/contests/artworks/${artId}/update-fields`, data)
  }

  async rotateArtwork(artId: number, degrees: number): Promise<{ message: string; artwork_url: string }> {
    return apiService.patch(`/contests/artworks/${artId}/rotate`, { degrees }).then((response) => response.data)
  }

  async cropArtwork(
    artId: number,
    crop: { x: number; y: number; width: number; height: number }
  ): Promise<{ message: string; artwork_url: string }> {
    return apiService.patch(`/contests/artworks/${artId}/crop`, crop).then((response) => response.data)
  }

  async sendArtReceiptEmail(artId: number): Promise<void> {
    return apiService.post(`/contests/artworks/${artId}/send-receipt-email`)
  }

  async registerPostAuctionSale(artId: number, data: FormData): Promise<void> {
    return apiService
      .post(`/arts/${artId}/post-auction-sale`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((response) => response.data)
  }

  async deleteArt(artId: number): Promise<void> {
    return apiService.delete(`/contests/artworks/${artId}`)
  }

  async openArtworkIdentifierPdf(artId: number): Promise<void> {
    return openBlobFromApi(`/contests/artworks/${artId}/identifier-pdf`)
  }

  async openArtworkLabelsPdf(contestId: number): Promise<void> {
    return openBlobFromApi(`/contests/${contestId}/artwork-labels-pdf`)
  }

  async printArtworkIdentifierPdf(artId: number): Promise<void> {
    return printBlobFromApi(`/contests/artworks/${artId}/identifier-pdf`)
  }

  async sendEmailNotifications(applicationIds: number[]): Promise<{
    message: string
    applications_ids: number[]
  }> {
    return apiService
      .post(`/contests/send-emails`, { application_ids: applicationIds })
      .then((response) => response.data)
  }

  async getEmailNotificationStatus(applicationIds: number[]): Promise<
    {
      application_id: number
      email_notification_status: 'not_sent' | 'pending' | 'sending' | 'sent' | 'failed'
    }[]
  > {
    const params = new URLSearchParams()

    for (const applicationId of applicationIds) {
      params.append('application_ids[]', applicationId.toString())
    }
    return apiService.get(`/contests/email-notification-status`, { params }).then((response) => response.data)
  }

  async getPickupEmailsPreview(contestId: number, force = false): Promise<IPickupEmailsStats> {
    return apiService
      .get(`/contests/${contestId}/pickup-emails/preview`, { params: { force } })
      .then((response) => response.data)
  }

  async sendPickupEmails(contestId: number, force = false): Promise<IPickupEmailsStats> {
    return apiService.post(`/contests/${contestId}/pickup-emails`, { force }).then((response) => response.data)
  }

  async getExhibitorCertificatesPreview(contestId: number, force = false): Promise<IExhibitorCertificatesStats> {
    return apiService
      .get(`/contests/${contestId}/exhibitor-certificates/preview`, { params: { force } })
      .then((response) => response.data)
  }

  async openExhibitorCertificatePreviewPdf(contestId: number, certificateDate: string): Promise<void> {
    const params = new URLSearchParams({ certificate_date: certificateDate })

    return openBlobFromApi(`/contests/${contestId}/exhibitor-certificates/preview-pdf?${params.toString()}`)
  }

  async sendExhibitorCertificates(
    contestId: number,
    payload: IExhibitorCertificatesPayload
  ): Promise<IExhibitorCertificatesStats> {
    return apiService.post(`/contests/${contestId}/exhibitor-certificates`, payload).then((response) => response.data)
  }

  async cancelApplication(applicationId: number): Promise<CancelContestApplicationResponse> {
    return apiService.post(`/contests/applications/${applicationId}/cancel`).then((response) => response.data)
  }

  /** Registers an application paid at the reception desk (ABRA-398): it is created already paid. */
  async createManualApplication(
    contestId: number,
    payload: IContestManualApplicationPayload
  ): Promise<IContestManualApplicationResponse> {
    return apiService.post(`/contests/${contestId}/applications/manual`, payload).then((response) => response.data)
  }

  async listArtworksForEvaluation(filter: IArtworkEvaluationFilter): Promise<{
    current_page: number
    per_page: number
    total: number
    total_filtered: number
    evaluators_count: number
    evaluated_count: number
    pending_count: number
    /** Quantas das obras filtradas o site está exibindo na vitrine (ABRA-314). */
    showcase_count: number
    /** Se o concurso está publicado na galeria — sem isso a vitrine não sai no site. */
    contest_shows_in_gallery: boolean
    /** Os slots já tomados no concurso inteiro, para o select de posição. */
    showcase_taken_positions: IShowcaseTakenPosition[]
    /** Quantos slots fixos a vitrine oferece — o mesmo teto do `max:` da API. */
    showcase_max_position: number
    data: IContestArtworkForEvaluation[]
  }> {
    const params = new URLSearchParams()
    if (filter.contest_id) params.append('contest_id', filter.contest_id)
    if (filter.applicant_id) params.append('applicant_id', filter.applicant_id)
    if (filter.search) params.append('search', filter.search)
    if (filter.exhibition_status) params.append('exhibition_status', filter.exhibition_status)
    if (filter.evaluation_status) params.append('evaluation_status', filter.evaluation_status)
    if (filter.showcase) params.append('showcase', filter.showcase)
    if (filter.order_by) params.append('order_by', filter.order_by)
    if (filter.order_direction) params.append('order_direction', filter.order_direction)
    params.append('page_size', filter.page_size.toString())
    params.append('page', filter.page.toString())

    return apiService.get('/contests/artworks-for-evaluation', { params }).then((response) => response.data)
  }

  /**
   * Cura a presença de uma obra na vitrine do site (ABRA-314).
   *
   * `showcase_override: null` é uma instrução de verdade — "volte a seguir a
   * regra automática" — e não a ausência do campo, então os dois campos vão
   * sempre no corpo.
   */
  async updateArtShowcase(artId: number, payload: IArtworkShowcasePayload): Promise<IArtworkShowcaseState> {
    return apiService.patch(`/contests/artworks/${artId}/showcase`, payload).then((response) => response.data.data)
  }

  async storeArtEvaluations(
    artId: number,
    payload: IArtEvaluationsBulkPayload
  ): Promise<{
    data: IArtEvaluationByEvaluator[]
    overall_average: number | null
    evaluation_status: 'evaluated' | 'pending'
  }> {
    return apiService.post(`/contests/artworks/${artId}/evaluations`, payload).then((response) => response.data)
  }

  /** Downloads the image of a single artwork, named after the artwork and its author. */
  async downloadArtworkImage(artId: number, fallbackFilename: string): Promise<void> {
    return downloadBlobFromApi(`/contests/artworks/${artId}/image-download`, fallbackFilename)
  }

  /**
   * Downloads a ZIP with the images of every artwork matching the evaluation filters —
   * the same selection the screen is showing, pagination aside.
   */
  async downloadArtworkImagesZip(filter: IArtworkEvaluationFilter, contestSlug: string): Promise<void> {
    const params = new URLSearchParams()
    if (filter.contest_id) params.append('contest_id', filter.contest_id)
    if (filter.applicant_id) params.append('applicant_id', filter.applicant_id)
    if (filter.search) params.append('search', filter.search)
    if (filter.exhibition_status) params.append('exhibition_status', filter.exhibition_status)
    if (filter.evaluation_status) params.append('evaluation_status', filter.evaluation_status)

    return downloadBlobFromApi(`/contests/artwork-images-zip?${params.toString()}`, `concurso_${contestSlug}_obras.zip`)
  }

  async downloadArtworkEvaluationsXlsx(contestId: number | string, contestSlug: string): Promise<void> {
    return downloadBlobFromApi(
      `/contests/${contestId}/artwork-evaluations/export-xlsx`,
      `concurso_${contestSlug}_avaliacoes.xlsx`
    )
  }

  /** Downloads a XLSX with auction transfer (repasse) data for all artworks of a given event. */
  async downloadAuctionRepasseXlsx(eventId: number): Promise<void> {
    return downloadBlobFromApi(
      `/contests/artworks/auction-repasse-xlsx?event_id=${eventId}`,
      `leilao_repasse_evento_${eventId}.xlsx`
    )
  }

  async listApplicantsForSelect(contestId?: string): Promise<IApplicantSelectOption[]> {
    const params = new URLSearchParams()
    if (contestId) params.append('contest_id', contestId)

    return apiService.get('/contests/applicants-for-select', { params }).then((response) => response.data)
  }
}
